import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Search, Download, User } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvestmentPlansLedgerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ rows: [], totals: { aum: 0, invested: 0, active_positions: 0, closed_positions: 0 } });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    api.get('/investment-plans/admin/ledger').then(r => setData(r.data))
      .catch(err => toast.error(formatError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const rows = data.rows.filter(r => {
    if (filter !== 'all' && r.status !== filter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (r.client_name || '').toLowerCase().includes(q)
      || (r.client_code || '').toLowerCase().includes(q)
      || (r.plan_name || '').toLowerCase().includes(q);
  });

  const exportCsv = () => {
    const header = ['Client Code', 'Client', 'Plan', 'Category', 'Mode', 'Units', 'Avg NAV', 'Current NAV', 'Invested', 'Current Value', 'Unrealized PnL', 'Status'];
    const lines = [header.join(',')].concat(rows.map(r => [
      r.client_code, `"${r.client_name || ''}"`, `"${r.plan_name || ''}"`, r.plan_category, r.plan_pricing_mode,
      Number(r.units).toFixed(6), Number(r.avg_nav || 0).toFixed(4), Number(r.current_nav).toFixed(4),
      Number(r.total_invested || 0).toFixed(2), Number(r.current_value).toFixed(2),
      Number(r.unrealized_pnl).toFixed(2), r.status,
    ].join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `investment-plans-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
    toast.success('Ledger exported');
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 size={24} className="animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="plans-ledger-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-brand-text-primary">Plans Ledger</h1>
          <p className="text-sm text-brand-text-secondary">Every investor position across every plan — for compliance review.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client or plan..." className="pl-9 h-9 w-72 text-sm" data-testid="ledger-search" />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)} className="h-9 text-sm border border-brand-border rounded px-2" data-testid="ledger-filter">
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
          <button onClick={exportCsv} className="h-9 px-3 border border-brand-border rounded text-sm flex items-center gap-1 hover:bg-brand-bg" data-testid="ledger-export"><Download size={13} /> CSV</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="AUM" value={money(data.totals.aum)} tone="ok" />
        <Stat label="Invested" value={money(data.totals.invested)} />
        <Stat label="Active positions" value={data.totals.active_positions} />
        <Stat label="Closed positions" value={data.totals.closed_positions} />
      </div>

      <Card className="border-brand-border bg-white">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs" data-testid="ledger-table">
            <thead><tr className="border-b border-brand-border bg-brand-bg/50">
              {['Client', 'Plan', 'Mode', 'Units', 'Avg NAV', 'Current NAV', 'Invested', 'Current Value', 'Unrealized', 'Status', ''].map(h => (
                <th key={h || 'act'} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={11} className="py-12 text-center text-brand-text-secondary">No matching positions</td></tr>
                : rows.map(r => (
                <tr key={r.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover" data-testid={`ledger-row-${r.id}`}>
                  <td className="py-2 px-3">
                    <div className="font-medium">{r.client_name}</div>
                    <div className="text-[10px] font-mono text-brand-primary">{r.client_code}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-medium">{r.plan_name}</div>
                    <div className="text-[10px] text-brand-text-secondary capitalize">{r.plan_category}</div>
                  </td>
                  <td className="py-2 px-3"><Badge className={`text-[9px] border-0 ${r.plan_pricing_mode === 'live' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>{r.plan_pricing_mode}</Badge></td>
                  <td className="py-2 px-3 font-mono">{Number(r.units).toFixed(6)}</td>
                  <td className="py-2 px-3 font-mono">${Number(r.avg_nav || 0).toFixed(4)}</td>
                  <td className="py-2 px-3 font-mono">${Number(r.current_nav).toFixed(4)}</td>
                  <td className="py-2 px-3 font-mono">{money(r.total_invested)}</td>
                  <td className="py-2 px-3 font-mono font-bold">{money(r.current_value)}</td>
                  <td className={`py-2 px-3 font-mono font-bold ${r.unrealized_pnl >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{r.unrealized_pnl >= 0 ? '+' : ''}{money(r.unrealized_pnl)}</td>
                  <td className="py-2 px-3"><Badge className={`text-[9px] border-0 ${r.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>{r.status}</Badge></td>
                  <td className="py-2 px-3">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/clients/${r.client_id}`)} className="h-7 px-2 text-xs text-brand-primary hover:bg-brand-primary/10" data-testid={`view-360-${r.id}`}>
                      <User size={12} className="mr-1" /> 360
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="bg-white border border-brand-border rounded-lg p-4">
    <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{label}</p>
    <p className={`font-heading text-2xl font-bold mt-1 ${tone === 'ok' ? 'text-brand-success' : 'text-brand-text-primary'}`}>{value}</p>
  </div>
);
